<script lang="ts">
	import type { ErrorResult } from '../utils/errors';

	let { 
		message,
		type = 'info',
		details = undefined,
		duration = 5000,
		dismissible = true,
		onDismiss = () => {}
	}: {
		message: string;
		type?: 'error' | 'warning' | 'success' | 'info';
		details?: string[];
		duration?: number;
		dismissible?: boolean;
		onDismiss?: () => void;
	} = $props();

	let visible = $state(true);
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	// Auto-dismiss after duration (unless it's an error which should stay visible)
	if (duration > 0 && type !== 'error') {
		timeoutId = setTimeout(() => {
			dismiss();
		}, duration);
	}

	function dismiss() {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		visible = false;
		onDismiss();
	}

	// Clean up timeout on component destroy
	$effect(() => {
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	});
</script>

{#if visible}
	<div class="toast toast-{type}" role="alert" aria-live="polite">
		<div class="toast-content">
			<div class="toast-icon">
				{#if type === 'error'}
					❌
				{:else if type === 'warning'}
					⚠️
				{:else if type === 'success'}
					✅
				{:else}
					ℹ️
				{/if}
			</div>
			
			<div class="toast-message">
				<div class="toast-text">{message}</div>
				{#if details && details.length > 0}
					<ul class="toast-details">
						{#each details as detail}
							<li>{detail}</li>
						{/each}
					</ul>
				{/if}
			</div>
			
			{#if dismissible}
				<button class="toast-dismiss" onclick={dismiss} aria-label="Melding sluiten">
					×
				</button>
			{/if}
		</div>
	</div>
{/if}

<style>
	.toast {
		position: fixed;
		top: 20px;
		right: 20px;
		min-width: 300px;
		max-width: 500px;
		padding: 0;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		z-index: 1000;
		animation: slideIn 0.3s ease-out;
	}

	@keyframes slideIn {
		from {
			transform: translateX(100%);
			opacity: 0;
		}
		to {
			transform: translateX(0);
			opacity: 1;
		}
	}

	.toast-content {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 16px;
	}

	.toast-icon {
		flex-shrink: 0;
		font-size: 20px;
	}

	.toast-message {
		flex-grow: 1;
	}

	.toast-text {
		font-weight: 500;
		margin-bottom: 4px;
	}

	.toast-details {
		margin: 8px 0 0 0;
		padding-left: 20px;
		font-size: 14px;
		opacity: 0.9;
	}

	.toast-details li {
		margin-bottom: 4px;
	}

	.toast-dismiss {
		flex-shrink: 0;
		background: none;
		border: none;
		font-size: 20px;
		cursor: pointer;
		padding: 0;
		width: 24px;
		height: 24px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
		transition: background-color 0.2s;
	}

	.toast-dismiss:hover {
		background-color: rgba(0, 0, 0, 0.1);
	}

	/* Type-specific styling */
	.toast-error {
		background-color: #fee;
		border-left: 4px solid #dc3545;
		color: #721c24;
	}

	.toast-warning {
		background-color: #fff3cd;
		border-left: 4px solid #ffc107;
		color: #856404;
	}

	.toast-success {
		background-color: #d4edda;
		border-left: 4px solid #28a745;
		color: #155724;
	}

	.toast-info {
		background-color: #e7f3ff;
		border-left: 4px solid #007bff;
		color: #004085;
	}
</style>